import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { useEffect, useRef } from "react"; // Import useRef
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  CapacitorPreferencesConstants,
  MetricConstants,
  MetricTypeConstants,
} from "./components/Constants";
import config from "./components/helpers/socket/config";
import optionGridConfig from "./platforms/cmed/OptionsGrid/optionsGridConfig";
import { pubsubEvents } from "./components/helpers/socket/pubsubEvent";
import { getCapacitorPreference } from "./utils/core-utils/capPreferencesUtils";
import { IS_MOBILE, IS_MOBILE_BROWSER } from "./utils/deviceDetectionUtil";
import { disconnect, send } from "./utils/sockets/asyncSocket";
import { OrdersDataType, OrdersType } from "./Constants";
import { startMetric } from "./utils/metric-utils/metricUtils";
import { unsubscribeStrikeLadder } from "./platforms/cmed/OptionsGrid/unsubscribeStrikeLadder";

const isWebMode = Capacitor.getPlatform().includes("web");

export default function AppEventListenerInitializer(props: any) {
  let history = useHistory();

  // Use useRef to create a persistent flag
  const hasUnsubscribed = useRef(false);

  useEffect(() => {
    let listenerHandle: any;

    const setupListener = async () => {
      listenerHandle = await App.addListener(
        "appStateChange",
        ({ isActive }) => {
          if (
            !isActive &&
            !isWebMode &&
            props.persistedOptionGrids?.length > 0
          ) {
            props.persistedOptionGrids.forEach((grid: any) => {
              unsubscribeStrikeLadder(grid);
            });
          }
        },
      );
    };

    setupListener();

    return () => {
      if (listenerHandle && typeof listenerHandle.remove === "function") {
        listenerHandle.remove();
      }
    };
  }, [props.persistedOptionGrids, isWebMode]);

  useEffect(() => {
    App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      console.log("appUrlOpen", event.url);
      if (
        (event.url.includes("mobileAuth") || event.url.includes("code")) &&
        IS_MOBILE_BROWSER
      ) {
        history.push("/mobileAuth" + "?" + event.url.split("?")[1]);
        void Browser.close();
      }
    });
  }, []);

  const unSubCMEDSubscriptions__exceptModals = () => {
    //market data 1
    let unSubRequestForTradingGridGroups = {
      action: optionGridConfig.socket.actions.CMED_ORDERS_UNSUBSCRIBE,
      type: OrdersType.ORDERS_DATA_UNSUBSCRIBE,
      requestID: uuidv4(),
      payload: {
        ordersDataType: "TradingGridGroupsV2",
      },
    };

    //market data 2
    let unSubRequestForTradingUserDetails = {
      action: optionGridConfig.socket.actions.CMED_ORDERS_UNSUBSCRIBE,
      type: OrdersType.ORDERS_DATA_UNSUBSCRIBE,
      requestID: uuidv4(),
      payload: {
        orderId: "",
        ordersDataType: OrdersDataType.TRADING_USER_DETAILS,
      },
    };

    //order book
    let unSubRequestForTradingOrders = {
      action: optionGridConfig.socket.actions.CMED_ORDERS_UNSUBSCRIBE,
      type: OrdersType.ORDERS_DATA_UNSUBSCRIBE,
      requestID: uuidv4(),
      payload: {
        ordersDataType: OrdersDataType.TRADING_ORDERS,
      },
    };

    //block feed
    let unSubRequestForTradingMarketActivities = {
      action: optionGridConfig.socket.actions.CMED_ORDERS_UNSUBSCRIBE,
      type: OrdersType.TRADING_MARKET_ACTIVITIES,
      requestID: uuidv4(),
      payload: {
        ordersDataType: OrdersDataType.TRADING_MARKET_ACTIVITIES,
      },
    };

    //positions
    let unSubRequestForTradingPositionAggregates = {
      action: "cmed/orders/unsubscribe",
      type: "ordersDataSubscribe",
      requestID: uuidv4(),
      payload: {
        ordersDataType: "TradingPositionAggregates",
      },
    };

    send(config.socket.channels.TRADING, unSubRequestForTradingGridGroups);
    send(config.socket.channels.TRADING, unSubRequestForTradingUserDetails);
    send(config.socket.channels.TRADING, unSubRequestForTradingOrders);
    send(
      config.socket.channels.TRADING,
      unSubRequestForTradingMarketActivities,
    );
    send(
      config.socket.channels.TRADING,
      unSubRequestForTradingPositionAggregates,
    );
  };

  App.addListener("appStateChange", async ({ isActive }) => {
    const value = await getCapacitorPreference(
      CapacitorPreferencesConstants.IS_LOGIN_COMPLETE,
    );
    const OB__orderId = await getCapacitorPreference(
      CapacitorPreferencesConstants.ORDERBOOK_MODAL_ORDERID,
    );
    const MD__insId = await getCapacitorPreference(
      CapacitorPreferencesConstants.MARKETDATA_MODAL_INSTRUMENT_ID,
    );
    const MD__gridGroupId = await getCapacitorPreference(
      CapacitorPreferencesConstants.MARKET_DATA_GRID_ID,
    );
    const OG__tsCode = await getCapacitorPreference(
      CapacitorPreferencesConstants.OPTIONS_GRID_TSCODE,
    );

    if (isActive === true) {
      value === "true" &&
        !isWebMode &&
        PubSub.publish(pubsubEvents.socketKeys.ATTEMPT_VISIBILITY_RECONNECT);
      value === "true" && !isWebMode && props.setIsReconnectingState(true);
      value === "true" &&
        !isWebMode &&
        startMetric(
          MetricConstants.TIME_TO_FIRST_PAINT_RECONNECTING_STATE_CHANGE,
          MetricTypeConstants.TIMER,
        );
      value !== "true" &&
        IS_MOBILE &&
        !window.location.href.includes("code") &&
        props.onLogin();
      // reset the state
      hasUnsubscribed.current = false;
    } else {
      /**
       * @NOTE
       * BELOW UNSUBS ARE NOT PAYLOAD PARAMETERIZED FOR ORDERBOOK/ MARKET DATA/ OPTIONS GRID
       */
      // Check if the function has already run
      if (!hasUnsubscribed.current && !isWebMode) {
        unSubCMEDSubscriptions__exceptModals();
        /**
         * @NOTE
         * BELOW UNSUBS ARE PAYLOAD PARAMETERIZED FOR ORDERBOOK/ MARKET DATA/ OPTIONS GRID
         * @TODO -> Find a way to handle when the the ids are not valid if the app is closed by the user
         */
        //orderbook view order details unsubscribe
        if (OB__orderId) {
          let unSubRequestForTradingOrderDetails = {
            action: optionGridConfig.socket.actions.CMED_ORDERS_UNSUBSCRIBE,
            type: OrdersType.ORDERS_DATA_UNSUBSCRIBE,
            requestID: uuidv4(),
            payload: {
              orderId: OB__orderId,
              ordersDataType: OrdersDataType.TRADING_ORDER_DETAILS,
            },
          };
          send(
            config.socket.channels.TRADING,
            unSubRequestForTradingOrderDetails,
          );
        }
        //marketdata view market overview unsubscribe
        if (MD__insId) {
          let unSubRequestForContractMarketData = {
            action: config.socket.actions.CMED_CONTRACT_MD_UNSUBSCRIBE,
            type: OrdersType.CONTRACT_MARKET_DATA,
            requestID: uuidv4(),
            payload: {
              ordersDataType: OrdersDataType.CONTRACT_MARKET_DATA,
              itemId: MD__insId,
            },
          };
          send(
            config.socket.channels.TRADING,
            unSubRequestForContractMarketData,
          );
        }
        //marketdata selected grid group unsubscribe
        if (MD__gridGroupId) {
          let unSubRequestForTradingGridGroup = {
            action: "cmed/orders/unsubscribe",
            type: "ordersDataUnsubscribe",
            requestID: uuidv4(),
            payload: {
              ordersDataType: "TradingGridGroupItemMarketDataV2",
              groupId: MD__gridGroupId,
            },
          };
          send(config.socket.channels.TRADING, unSubRequestForTradingGridGroup);
        }
        //options grid view modal unsubscribe
        if (OG__tsCode) {
          let unSubRequestForOptionsGridGroupItemDepth = {
            action:
              optionGridConfig.socket.actions
                .CMED_UNSUBSCRIBE_STRIKE_LADDER_DEPTH,
            type: OrdersType.OPTIONS_GRID_GROUP_ITEM_DEPTH,
            requestID: uuidv4(),
            payload: {
              termStructureCode: OG__tsCode,
            },
          };
          send(
            config.socket.channels.TRADING,
            unSubRequestForOptionsGridGroupItemDepth,
          );
        }
        hasUnsubscribed.current = true; // Set the flag to true after running
        disconnect();
      }
    }
  });

  return <div></div>;
}
